import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, getToken } from '../lib/api';

/**
 * Department-wise KRA & KPI Performance (MIS WND / WNDOT).
 *
 *  - Planned: work due in the period; Actual: done; Pending (WND): not done
 *  - On time (Met) / Late (Missed): done against its target
 *
 * Three scores, each as a gap - 0 is perfect, the minus is what is short:
 *  - KRA %      on time:    Met / (Met + Missed) − 100   (WNDOT)
 *  - KPI %      work done:  Actual / Planned − 100       (WND)
 *  - Combined   the two averaged
 * CRM is measured on one thing only: DOD met against missed (pending is
 * counted, not scored) - its DOD score, kept out of the three above.
 * The colours key on the underlying %: green 80+, amber 50-80, red below.
 */

interface DepartmentSummaryRow {
  Department: string;
  KRA: string;
  Planned: number;
  Actual: number;
  Pending: number;
  Met: number;
  Missed: number;
  'Met %': string | null;
}

interface DesignRow { Designer: string; 'Total Jobs': number; Met: number; Missed: number; Pending: number; 'Met %': string | null }
interface PurchaseRow { 'Board Status': string; 'Total Orders': number; Met: number; Missed: number; Pending: number; 'Met %': string | null }
interface PpcRow { Status: string; 'Total Orders': number; Met: number; Missed: number; Pending: number; 'Met %': string | null }
interface Row { CRM: string; Month: string; 'Total Dispatched': number; Met: number; Missed: number; Pending: number; 'Met %': string | null }

const TOTAL_ROW = 'ALL — total';
const ALL_DEPTS = 'All departments';

/** The three scores, as plain percentages (100 = perfect). */
interface Scores { kra: number | null; kpi: number | null; combined: number | null }

/** Planned work, how much of it is done, and how much of the done was on time. */
function scoresOf(planned: number, pending: number, met: number, missed: number): Scores {
  // KRA: done on time.  KPI: work done against planned.
  const kra = met + missed > 0 ? (met * 100) / (met + missed) : null;
  const kpi = planned > 0 ? ((planned - pending) * 100) / planned : null;
  const combined = kra != null && kpi != null ? (kra + kpi) / 2 : kra ?? kpi;
  return { kra, kpi, combined };
}

/** Each department counting once. */
function averageScores(list: Scores[]): Scores {
  const avg = (k: keyof Scores) => {
    const v = list.map((s) => s[k]).filter((n): n is number => n != null);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  };
  return { kra: avg('kra'), kpi: avg('kpi'), combined: avg('combined') };
}

/** Red below half, amber to 80, green above. */
function tone(pct: number | null): string {
  if (pct == null) return 'bg-slate-100 text-slate-500';
  if (pct >= 80) return 'bg-emerald-100 text-emerald-800';
  if (pct >= 50) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-700';
}

/** "-20.9%" (79.1% done), or a dash when there is nothing to judge yet. */
function gapText(pct: number | null): string {
  if (pct == null) return '—';
  const g = pct - 100;
  return `${Math.abs(g) < 0.05 ? '0.0' : g.toFixed(1)}%`;
}

const SCORE_HELP = {
  kra: 'KRA % - on time: On time ÷ (On time + Late). 0% is all on time; -20% means a fifth was late.',
  kpi: 'KPI % - work done: Actual ÷ Planned. 0% is all done; -20% means a fifth is still pending.',
  combined: 'Combined - KRA % and KPI % averaged.',
  dod: 'DOD score - DOD met ÷ (met + missed). Pending is counted, not scored. 0% is every DOD met.',
};

/** CRM's one measure: DOD met against missed. */
function dodPct(met: number, missed: number): number | null {
  return met + missed > 0 ? (met * 100) / (met + missed) : null;
}

/** CRM's score in place of the three score columns. */
function DodCell({ pct, small, label }: { pct: number | null; small?: boolean; label?: boolean }) {
  return (
    <td colSpan={3} className={`px-3 ${small ? 'py-1.5' : 'py-2.5'} text-right`}>
      {label && <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">DOD score</span>}
      <Badge pct={pct} help={SCORE_HELP.dod} strong />
    </td>
  );
}

function Badge({ pct, help, strong }: { pct: number | null; help: string; strong?: boolean }) {
  return (
    <span
      title={pct == null ? `${help} Nothing to judge yet.` : `${help} (${pct.toFixed(1)}%)`}
      className={`inline-block min-w-[4.25rem] rounded px-2 py-0.5 text-center text-xs font-semibold tabular-nums ${tone(pct)} ${strong ? 'ring-1 ring-inset ring-current/30' : ''}`}
    >
      {gapText(pct)}
    </span>
  );
}

/** The three score cells of a table row. */
function ScoreCells({ s }: { s: Scores }) {
  return (
    <>
      <td className="px-3 py-2.5 text-right"><Badge pct={s.kra} help={SCORE_HELP.kra} /></td>
      <td className="px-3 py-2.5 text-right"><Badge pct={s.kpi} help={SCORE_HELP.kpi} /></td>
      <td className="px-3 py-2.5 text-right"><Badge pct={s.combined} help={SCORE_HELP.combined} strong /></td>
    </>
  );
}

const SCORE_HEADS = (
  <>
    <th className="px-3 py-2.5 text-right" title={SCORE_HELP.kra}>KRA %<div className="text-[10px] font-normal normal-case text-slate-400">on time</div></th>
    <th className="px-3 py-2.5 text-right" title={SCORE_HELP.kpi}>KPI %<div className="text-[10px] font-normal normal-case text-slate-400">work done</div></th>
    <th className="px-3 py-2.5 text-right" title={SCORE_HELP.combined}>Combined<div className="text-[10px] font-normal normal-case text-slate-400">KRA + KPI</div></th>
  </>
);

/** KRA / KPI / Combined, labelled, for a department heading. */
function ScoreStrip({ s, dod }: { s: Scores; dod?: number | null }) {
  const item = (label: string, pct: number | null, help: string, strong?: boolean) => (
    <span className="flex items-center gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-white/70">{label}</span>
      <Badge pct={pct} help={help} strong={strong} />
    </span>
  );
  if (dod !== undefined) return <span className="flex items-center gap-3">{item('DOD score', dod, SCORE_HELP.dod, true)}</span>;
  return (
    <span className="flex flex-wrap items-center gap-3">
      {item('KRA', s.kra, SCORE_HELP.kra)}
      {item('KPI', s.kpi, SCORE_HELP.kpi)}
      {item('Combined', s.combined, SCORE_HELP.combined, true)}
    </span>
  );
}

/** A department card: a heading with its counts and scores, and its rows. */
function DeptCard({ title, sub, counts, scores, dod, open, onToggle, children }: {
  title: string; sub?: string; counts: ReactNode; scores: Scores; open: boolean; onToggle: () => void; children: ReactNode;
  /** Given (CRM): this one score instead of KRA / KPI / Combined. */
  dod?: number | null;
}) {
  return (
    <div className="card overflow-hidden">
      <button type="button" className="card-header flex w-full flex-wrap items-center justify-between gap-3 text-left" onClick={onToggle} aria-expanded={open}>
        <span className="flex items-center gap-2">
          <span className="text-xs text-white/70">{open ? '▾' : '▸'}</span>
          <span>
            <span className="text-white">{title}</span>
            {sub && <span className="ml-2 text-xs font-normal text-white/70">{sub}</span>}
            <span className="block text-xs font-normal text-white/80">{counts}</span>
          </span>
        </span>
        <ScoreStrip s={scores} dod={dod} />
      </button>
      {open && children}
    </div>
  );
}

function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function DodReportPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [applied, setApplied] = useState<{ from: string; to: string }>({ from: '', to: '' });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [openDepts, setOpenDepts] = useState<Set<string>>(new Set(['crm', 'design', 'purchase', 'ppc']));

  function toggleDept(dept: string) {
    setOpenDepts((prev) => {
      const n = new Set(prev);
      if (n.has(dept)) n.delete(dept); else n.add(dept);
      return n;
    });
  }

  const report = <T,>(mode: string, pageSize: number) => () => {
    const sp = new URLSearchParams({ mode, page: '1', pageSize: String(pageSize) });
    if (applied.from) sp.set('from', applied.from);
    if (applied.to) sp.set('to', applied.to);
    return api<{ rows: T[]; total: number }>(`/api/reports?${sp.toString()}`);
  };
  const summaryQ = useQuery({ queryKey: ['department-kpi-summary', applied], queryFn: report<DepartmentSummaryRow>('department-kpi-summary', 10) });
  const q = useQuery({ queryKey: ['dod-report', applied], queryFn: report<Row>('crm-dod-monthly', 200) });
  const dq = useQuery({ queryKey: ['design-kpi', applied], queryFn: report<DesignRow>('design-kpi', 200) });
  const pq = useQuery({ queryKey: ['purchase-kpi', applied], queryFn: report<PurchaseRow>('purchase-kpi', 200) });
  const ppcQ = useQuery({ queryKey: ['ppc-kpi', applied], queryFn: report<PpcRow>('ppc-kpi', 200) });

  const deptRows = (summaryQ.data?.rows ?? []).filter((r) => r.Department !== ALL_DEPTS);
  const deptScores = (r: DepartmentSummaryRow) => scoresOf(r.Planned, r.Pending, r.Met, r.Missed);
  // CRM is scored on DOD alone - the KRA / KPI averages are the other departments.
  const overall = averageScores(deptRows.filter((r) => r.Department !== 'CRM').map(deptScores));
  const crmRow = deptRows.find((r) => r.Department === 'CRM');
  const crmDod = crmRow ? dodPct(crmRow.Met, crmRow.Missed) : null;
  const summaryOf = (dept: string) => deptRows.find((r) => r.Department === dept);
  /** A department heading's scores - the same figures as its summary line. */
  const headScores = (dept: string): Scores => {
    const r = summaryOf(dept);
    return r ? deptScores(r) : { kra: null, kpi: null, combined: null };
  };
  const kraText = (dept: string) => summaryOf(dept)?.KRA;

  /** Fold the flat rows into one group per CRM: its total, then its months. */
  const groups = useMemo(() => {
    const byCrm = new Map<string, { total: Row | null; months: Row[] }>();
    for (const r of q.data?.rows ?? []) {
      const g = byCrm.get(r.CRM) ?? { total: null, months: [] };
      if (r.Month === TOTAL_ROW) g.total = r; else g.months.push(r);
      byCrm.set(r.CRM, g);
    }
    const crmScore = (t: Row | null) => (t ? dodPct(t.Met, t.Missed) : null);
    // Weakest first: the ones to talk to.
    return [...byCrm.entries()].sort((a, b) => (crmScore(a[1].total) ?? 999) - (crmScore(b[1].total) ?? 999));
  }, [q.data]);

  const tally = <T extends { Met: number; Missed: number; Pending: number }>(rows: T[] | undefined, total: (r: T) => number) => {
    let met = 0, missed = 0, pending = 0, all = 0;
    for (const r of rows ?? []) { met += r.Met; missed += r.Missed; pending += r.Pending; all += total(r); }
    return { met, missed, pending, total: all };
  };
  const crmT = useMemo(() => tally(groups.map(([, g]) => g.total).filter((t): t is Row => !!t), (r) => r['Total Dispatched']), [groups]);
  const designT = useMemo(() => tally(dq.data?.rows, (r) => r['Total Jobs']), [dq.data]);
  const purchaseT = useMemo(() => tally(pq.data?.rows, (r) => r['Total Orders']), [pq.data]);
  const ppcT = useMemo(() => tally(ppcQ.data?.rows, (r) => r['Total Orders']), [ppcQ.data]);
  const countsText = (label: string, t: { met: number; missed: number; pending: number; total: number }) =>
    `${label}: ${t.total} · On time ${t.met} · Late ${t.missed} · Pending ${t.pending}`;

  function preset(kind: 'week' | 'month' | 'lastMonth' | 'all') {
    const now = new Date();
    if (kind === 'all') { setFrom(''); setTo(''); setApplied({ from: '', to: '' }); return; }
    let f: Date, t: Date;
    if (kind === 'week') {
      f = new Date(now); f.setDate(now.getDate() - now.getDay()); t = now;
    } else if (kind === 'month') {
      f = startOfMonth(now); t = now;
    } else {
      f = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      t = new Date(now.getFullYear(), now.getMonth(), 0);
    }
    setFrom(iso(f)); setTo(iso(t)); setApplied({ from: iso(f), to: iso(t) });
  }

  function exportXlsx(mode: string, name: string) {
    const sp = new URLSearchParams({ mode });
    if (applied.from) sp.set('from', applied.from);
    if (applied.to) sp.set('to', applied.to);
    const token = getToken();
    fetch(`/api/reports/export.xlsx?${sp.toString()}`, { headers: token ? { authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${name}-${iso(new Date())}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  const rangeText = applied.from || applied.to
    ? `${applied.from || 'start'} to ${applied.to || 'today'}`
    : 'All time (scoring begins 24-Sep-2026)';
  const emptyRow = (cols: number, loading: boolean, what: string) => (
    <tr><td colSpan={cols} className="p-4 text-center text-slate-500">
      {loading ? `Loading ${what}…` : 'Tracking starts 24-Sep-2026. Earlier orders count as met and are not scored.'}
    </td></tr>
  );
  const countCells = (met: number, missed: number, pending: number) => (
    <>
      <td className="px-3 py-2.5 text-right font-semibold text-emerald-700">{met}</td>
      <td className="px-3 py-2.5 text-right font-medium text-red-700">{missed}</td>
      <td className="px-3 py-2.5 text-right font-medium text-amber-700">{pending}</td>
    </>
  );

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-brand">Department Performance</h1>
          <p className="max-w-3xl text-sm text-slate-500">
            KRA (done on time) and KPI (work done against planned) for CRM delivery dates, Design artwork, Purchase board
            and PPC job cards. Targets come from Settings → SLA Rules; office hours skip nights, Sundays and holidays.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost text-sm" onClick={() => exportXlsx('department-kpi-summary', 'department-scores')}>Export summary</button>
          <button className="btn-ghost text-sm" onClick={() => exportXlsx('crm-dod-monthly', 'crm-dod-monthly')}>Export CRM monthly</button>
        </div>
      </div>

      <form className="card card-body flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); setApplied({ from, to }); }}>
        <div>
          <label className="label">From</label>
          <input type="date" className="input py-1" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input py-1" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="btn-primary" type="submit">Apply</button>
        <span className="mx-1 text-slate-300">|</span>
        <button type="button" className="btn-ghost py-1 text-xs" onClick={() => preset('week')}>This week</button>
        <button type="button" className="btn-ghost py-1 text-xs" onClick={() => preset('month')}>This month</button>
        <button type="button" className="btn-ghost py-1 text-xs" onClick={() => preset('lastMonth')}>Last month</button>
        <button type="button" className="btn-ghost py-1 text-xs" onClick={() => preset('all')}>Clear (all time)</button>
      </form>

      {/* The headline: one combined score for the company, and its two halves. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {([
          ['Combined score', overall.combined, 'KRA and KPI averaged over Design, Purchase, PPC and Quality', SCORE_HELP.combined],
          ['KRA % - on time', overall.kra, 'On time against judged, departments averaged', SCORE_HELP.kra],
          ['KPI % - work done', overall.kpi, 'Actual against planned, departments averaged', SCORE_HELP.kpi],
          ['CRM - DOD score', crmDod, crmRow ? `DOD met ${crmRow.Met} · missed ${crmRow.Missed} · pending ${crmRow.Pending}` : 'DOD met against missed', SCORE_HELP.dod],
        ] as const).map(([label, pct, sub, help]) => (
          <div key={label} className="card card-body" title={help}>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className={`rounded px-2 py-0.5 text-2xl font-bold tabular-nums ${tone(pct)}`}>{gapText(pct)}</span>
              {pct != null && <span className="text-sm text-slate-500">{pct.toFixed(1)}% achieved</span>}
            </div>
            <div className="mt-1 text-xs text-slate-400">{sub} · {rangeText}</div>
          </div>
        ))}
      </div>

      {/* Department summary */}
      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <span className="text-sm font-semibold text-slate-800">Department KRA &amp; KPI summary</span>
          <span className="text-xs text-slate-500">0% is perfect - the minus is the gap. Hover a score for the % achieved. CRM: DOD score only.</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-[11px] uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2.5">Department</th>
                <th className="px-3 py-2.5">Key result area</th>
                <th className="px-3 py-2.5 text-right">Planned</th>
                <th className="px-3 py-2.5 text-right">Actual</th>
                <th className="px-3 py-2.5 text-right text-emerald-700">On time</th>
                <th className="px-3 py-2.5 text-right text-red-700">Late</th>
                <th className="px-3 py-2.5 text-right text-amber-700">Pending</th>
                {SCORE_HEADS}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summaryQ.isLoading && <tr><td colSpan={10} className="p-4 text-center text-slate-400">Loading summary…</td></tr>}
              {deptRows.map((r) => (
                <tr key={r.Department} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-semibold text-slate-800">{r.Department}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-600">{r.KRA}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{r.Planned}</td>
                  <td className="px-3 py-2.5 text-right">{r.Actual}</td>
                  {countCells(r.Met, r.Missed, r.Pending)}
                  {r.Department === 'CRM' ? <DodCell pct={dodPct(r.Met, r.Missed)} label /> : <ScoreCells s={deptScores(r)} />}
                </tr>
              ))}
              {deptRows.length > 0 && (
                <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                  <td className="px-3 py-2.5 text-slate-800">Design, Purchase, PPC &amp; Quality</td>
                  <td className="px-3 py-2.5 text-xs font-normal text-slate-500">Each counts once · CRM is scored on DOD alone</td>
                  <td className="px-3 py-2.5" colSpan={5} />
                  <ScoreCells s={overall} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CRM */}
      {groups.length > 0 && (
        <DeptCard title="CRM" sub={kraText('CRM')} counts={countsText('Dispatched', crmT)} scores={headScores('CRM')} dod={crmDod}
                  open={openDepts.has('crm')} onToggle={() => toggleDept('crm')}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2.5">CRM · weakest first</th>
                  <th className="px-3 py-2.5 text-right">Dispatched</th>
                  <th className="px-3 py-2.5 text-right text-emerald-700">On time</th>
                  <th className="px-3 py-2.5 text-right text-red-700">Late</th>
                  <th className="px-3 py-2.5 text-right text-amber-700">Pending</th>
                  <th className="px-3 py-2.5 text-right" colSpan={3} title={SCORE_HELP.dod}>DOD score<div className="text-[10px] font-normal normal-case text-slate-400">met ÷ (met + missed)</div></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map(([crm, g]) => {
                  const t = g.total;
                  const isOpen = open.has(crm);
                  return (
                    <Fragment key={crm}>
                      <tr className="cursor-pointer hover:bg-slate-50" aria-expanded={isOpen}
                          onClick={() => setOpen((x) => { const n = new Set(x); if (n.has(crm)) n.delete(crm); else n.add(crm); return n; })}>
                        <td className="px-3 py-2.5 font-medium text-slate-800">
                          <span className="mr-1.5 text-xs text-slate-400">{isOpen ? '▾' : '▸'}</span>{crm}
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium">{t?.['Total Dispatched'] ?? 0}</td>
                        {countCells(t?.Met ?? 0, t?.Missed ?? 0, t?.Pending ?? 0)}
                        <DodCell pct={t ? dodPct(t.Met, t.Missed) : null} />
                      </tr>
                      {isOpen && g.months.map((m) => (
                        <tr key={`${crm}-${m.Month}`} className="bg-slate-50/70 text-xs">
                          <td className="py-1.5 pl-9 pr-3 text-slate-600">{m.Month}</td>
                          <td className="px-3 py-1.5 text-right">{m['Total Dispatched']}</td>
                          {countCells(m.Met, m.Missed, m.Pending)}
                          <DodCell pct={dodPct(m.Met, m.Missed)} small />
                        </tr>
                      ))}
                      {isOpen && g.months.length === 0 && (
                        <tr className="bg-slate-50/70"><td colSpan={8} className="py-2 pl-9 text-xs text-slate-500">No monthly rows in this period.</td></tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DeptCard>
      )}

      {/* Design */}
      <DeptCard title="Design" sub={kraText('Design')} counts={countsText('Jobs', designT)} scores={headScores('Design')}
                open={openDepts.has('design')} onToggle={() => toggleDept('design')}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Designer</th>
                <th className="px-3 py-2.5 text-right">Jobs</th>
                <th className="px-3 py-2.5 text-right text-emerald-700">On time</th>
                <th className="px-3 py-2.5 text-right text-red-700">Late</th>
                <th className="px-3 py-2.5 text-right text-amber-700">Pending</th>
                {SCORE_HEADS}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!dq.data?.rows?.length && emptyRow(8, dq.isLoading, 'designers')}
              {dq.data?.rows.map((r) => (
                <tr key={r.Designer} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r.Designer}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{r['Total Jobs']}</td>
                  {countCells(r.Met, r.Missed, r.Pending)}
                  <ScoreCells s={scoresOf(r['Total Jobs'], r.Pending, r.Met, r.Missed)} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DeptCard>

      {/* Purchase */}
      <DeptCard title="Purchase" sub={kraText('Purchase')} counts={countsText('Orders', purchaseT)} scores={headScores('Purchase')}
                open={openDepts.has('purchase')} onToggle={() => toggleDept('purchase')}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Board status</th>
                <th className="px-3 py-2.5 text-right">Orders</th>
                <th className="px-3 py-2.5 text-right text-emerald-700">On time</th>
                <th className="px-3 py-2.5 text-right text-red-700">Late</th>
                <th className="px-3 py-2.5 text-right text-amber-700">Pending</th>
                {SCORE_HEADS}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!pq.data?.rows?.length && emptyRow(8, pq.isLoading, 'purchase')}
              {pq.data?.rows.map((r) => (
                <tr key={r['Board Status']} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r['Board Status']}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{r['Total Orders']}</td>
                  {countCells(r.Met, r.Missed, r.Pending)}
                  <ScoreCells s={scoresOf(r['Total Orders'], r.Pending, r.Met, r.Missed)} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DeptCard>

      {/* PPC */}
      <DeptCard title="PPC" sub={kraText('PPC')} counts={countsText('Orders', ppcT)} scores={headScores('PPC')}
                open={openDepts.has('ppc')} onToggle={() => toggleDept('ppc')}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5 text-right">Orders</th>
                <th className="px-3 py-2.5 text-right text-emerald-700">On time</th>
                <th className="px-3 py-2.5 text-right text-red-700">Late</th>
                <th className="px-3 py-2.5 text-right text-amber-700">Pending</th>
                {SCORE_HEADS}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!ppcQ.data?.rows?.length && emptyRow(8, ppcQ.isLoading, 'PPC')}
              {ppcQ.data?.rows.map((r) => (
                <tr key={r.Status} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 font-medium text-slate-800">{r.Status}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{r['Total Orders']}</td>
                  {countCells(r.Met, r.Missed, r.Pending)}
                  <ScoreCells s={scoresOf(r['Total Orders'], r.Pending, r.Met, r.Missed)} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DeptCard>
    </div>
  );
}
